import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { AlertCircle } from '@lifeomic/chromicons';
import type { CombinedQueriesResult } from '@lifeomic/one-query';
import type { QueryObserverResult } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useFeatures } from '../../../../../hooks';
import { apiHooks } from '../../../../../hooks/useRestAPIHooks';
import { ModelSelect } from '../../../../AITemplates/GenerationMenu/ModelSelect';
import { useCRDTemplates } from '../../../../AITemplates/hooks/useCompositeReportDefinitionTemplates';
import { useTemplatingItemDetailQuery } from '../../../../AITemplates/hooks/useTemplatingItem';
import {
  CompositeReportDefinitionDetail,
  TemplateSummary,
} from '../../../../AITemplates/models';
import { KnownImpressionTypes } from '../../AITools/ImpressionEditor/ImpressionItem';
import { ExecutiveSummaryData } from '../executive-summary';
import { messages } from '../messages';
import { useCrdTemplateSectionAvailability } from '../useCrdTemplateSectionAvailability';
import { useRecursiveCRDFetch } from '../useRecursiveCRDFetch';
import { isAiGenerated } from '../utils';
import { CRDSelect } from './CRDSelect';
import { GenerateExecutiveSummary } from './GenerateExecutiveSummary';
import { ImpressionTypeSelect } from './ImpressionTypeSelect';
import { Instructions } from './Instructions';
import { RegenerateActionItems } from './RegenerateActionItems';

const useStyles = makeStyles(() => ({
  modalBox: {
    overflow: 'visible',
  },
}));

export type ImpressionType = KnownImpressionTypes | 'all';

export type ImpressionTypes =
  | {
      state: 'loading';
      data: null;
    }
  | {
      state: 'loaded';
      data: ImpressionType[];
    };

export type GenerateMenuFormProps = {
  onDismiss: () => void;
  patientId: string;
  executiveSummary?: ExecutiveSummaryData;
  isActionItemsRegenerate?: boolean;
};

type GenerateMenuModalProps = GenerateMenuFormProps & {
  isOpen: boolean;
};

export type GenerateMenuFormState = {
  crdDurableId: string | undefined;
  setCrdDurableId: (value: string | undefined) => void;
  instructions: string;
  setInstructions: (value: string) => void;
  impressionType: ImpressionType;
  setImpressionType: (value: ImpressionType) => void;
  modelId: string | undefined;
  setModelId: (value: string | undefined) => void;
  showModelSelect: boolean;
  models: { model_id: string; model_label: string }[];
  isLoadingCrds: boolean;
  compositeReportDefinitions: ReturnType<
    typeof useRecursiveCRDFetch
  >['compositeReportDefinitions'];
  impressionTypes: ImpressionTypes;
  isRegenerate: boolean;
  currentImpressionStatus: 'ai-generated' | 'human-edited' | 'missing';
  impressionWarning: string | null;
  generateDisabled: boolean;
  executiveSummary?: ExecutiveSummaryData;
  isActionItemsRegenerate: boolean;
  isLoadingCrdSectionInfo: boolean;
  hasTemplateSections: (durableId: string) => boolean | undefined;
  selectedCrdHasNoTemplateSections: boolean;
  compositeSectionLoadWarning: string | null;
};

export type GenerateMenuActionContext = {
  patientId: string;
  onDismiss: () => void;
};

export function useGenerateMenuForm({
  onDismiss,
  patientId,
  executiveSummary,
  isActionItemsRegenerate = false,
}: GenerateMenuFormProps): GenerateMenuFormState & GenerateMenuActionContext {
  const { formatMessage } = useIntl();
  const { isLoading: isLoadingCrds, compositeReportDefinitions } =
    useRecursiveCRDFetch();
  const { isLoading: isLoadingCrdSectionInfo, hasTemplateSections } =
    useCrdTemplateSectionAvailability(compositeReportDefinitions);
  const [crdDurableId, setCrdDurableId] = useState<string | undefined>(
    () => executiveSummary?.compositeReportDefinitionDurableId,
  );
  const [instructions, setInstructions] = useState('');
  const [impressionType, setImpressionType] = useState<ImpressionType>('all');
  const [modelId, setModelId] = useState<string | undefined>(undefined);
  const hasFeature = useFeatures();
  const showModelSelect = hasFeature('zoriModelSelection');
  const modelsQuery = apiHooks.useAPIQuery('GET /v1/agents-v2/models', {}) as {
    data?: {
      models: { model_id: string; model_label: string }[];
    };
  };

  useEffect(() => {
    if (crdDurableId) return;

    if (executiveSummary?.compositeReportDefinitionDurableId) {
      setCrdDurableId(executiveSummary.compositeReportDefinitionDurableId);
      return;
    }

    if (isLoadingCrds || isLoadingCrdSectionInfo) return;

    const firstUsable = compositeReportDefinitions.find(
      c => hasTemplateSections(c.durableId) === true,
    );
    if (firstUsable) {
      setCrdDurableId(firstUsable.durableId);
      return;
    }

    if (compositeReportDefinitions.length > 0) {
      setCrdDurableId(compositeReportDefinitions[0].durableId);
    }
  }, [
    isLoadingCrds,
    isLoadingCrdSectionInfo,
    executiveSummary?.compositeReportDefinitionDurableId,
    compositeReportDefinitions,
    crdDurableId,
    hasTemplateSections,
  ]);

  const { data: crd, isLoading: isLoadingCrd } = useTemplatingItemDetailQuery({
    durableId: crdDurableId,
  });
  const templateFetch = useCRDTemplates({
    compositeReportDefinition: crd as
      | CompositeReportDefinitionDetail
      | undefined,
  }) as CombinedQueriesResult<QueryObserverResult<TemplateSummary>[]>;
  const templateQueries = templateFetch.queries;

  const compositeCrd =
    crd?.type === 'compositeReportDefinition'
      ? (crd as CompositeReportDefinitionDetail)
      : undefined;

  const isLoadingTemplates = useMemo(() => {
    if (!compositeCrd?.sections.length) {
      return false;
    }
    if (templateQueries.length < compositeCrd.sections.length) {
      return true;
    }
    return compositeCrd.sections.some((_section, index) => {
      const query = templateQueries[index];
      return query?.isLoading === true;
    });
  }, [compositeCrd, templateQueries]);

  const templates = useMemo((): TemplateSummary[] => {
    if (!compositeCrd?.sections.length) {
      return [];
    }
    const fromQueries = compositeCrd.sections
      .map((_section, index) => {
        const query = templateQueries[index];
        if (!query || query.status === 'error') {
          return undefined;
        }
        if (query.data) {
          return query.data as TemplateSummary;
        }
        return undefined;
      })
      .filter((t): t is TemplateSummary => Boolean(t));

    if (fromQueries.length > 0) {
      return fromQueries;
    }
    if (
      !templateFetch.isError &&
      Array.isArray(templateFetch.data) &&
      templateFetch.data.length > 0
    ) {
      return templateFetch.data.filter(Boolean) as TemplateSummary[];
    }
    return [];
  }, [
    compositeCrd,
    templateFetch.data,
    templateFetch.isError,
    templateQueries,
  ]);

  const templateImpressionTypes = useMemo(
    () =>
      templates
        .map(template => template.impressionType)
        .filter((impressionType): impressionType is KnownImpressionTypes =>
          Boolean(impressionType),
        ),
    [templates],
  );

  const executiveSummaryImpressionTypes = useMemo(
    () =>
      executiveSummary?.impressions.map(
        impression => impression.impressionType,
      ) ?? [],
    [executiveSummary?.impressions],
  );

  const availableImpressionTypes = useMemo(() => {
    const source =
      templateImpressionTypes.length > 0
        ? templateImpressionTypes
        : executiveSummaryImpressionTypes;
    return Array.from(new Set(source));
  }, [executiveSummaryImpressionTypes, templateImpressionTypes]);

  const unresolvedTemplateSectionIds = useMemo(() => {
    if (!compositeCrd?.sections.length || isLoadingTemplates) {
      return [];
    }
    const failed: string[] = [];
    compositeCrd.sections.forEach((section, index) => {
      const query = templateQueries[index];
      if (!query) {
        failed.push(section.templateId);
        return;
      }
      if (query.status === 'error') {
        failed.push(section.templateId);
        return;
      }
      if (query.status === 'success' && !query.data) {
        failed.push(section.templateId);
      }
    });
    return failed;
  }, [compositeCrd, isLoadingTemplates, templateQueries]);
  const isLoadingCrdData =
    isLoadingCrds ||
    (crdDurableId !== undefined && (isLoadingCrd || isLoadingTemplates));

  const impressionTypes: ImpressionTypes = useMemo(() => {
    if (isLoadingCrdData) {
      return { state: 'loading' as const, data: null };
    } else {
      return {
        state: 'loaded' as const,
        data: ['all', ...availableImpressionTypes] as ImpressionType[],
      };
    }
  }, [availableImpressionTypes, isLoadingCrdData]);

  const isRegenerate =
    executiveSummary !== undefined &&
    executiveSummary.hasContent &&
    executiveSummary.status === 'active';

  const currentImpressionStatus = useMemo(() => {
    if (impressionType === 'all') return 'ai-generated';
    const impression = executiveSummary?.impressions.find(
      impression => impression.impressionType === impressionType,
    );
    return impression === undefined
      ? 'missing'
      : isAiGenerated(impression.impression)
      ? 'ai-generated'
      : 'human-edited';
  }, [executiveSummary?.impressions, impressionType]);

  const { warning: impressionWarning, generateDisabled } = useImpressionWarning(
    {
      currentImpressionStatus,
      isRegenerate,
      executiveSummary,
      isActionItemsRegenerate,
      selectedImpressionType: impressionType,
      hasInstructions: instructions.trim() !== '',
    },
  );

  /** Default CRD is chosen in an effect; avoid an enabled generate click before it runs. */
  const awaitingCompositeCrdSelection = useMemo(
    () =>
      !isActionItemsRegenerate &&
      !executiveSummary?.compositeReportDefinitionDurableId &&
      compositeReportDefinitions.length > 0 &&
      (isLoadingCrds || isLoadingCrdSectionInfo || crdDurableId === undefined),
    [
      isActionItemsRegenerate,
      executiveSummary?.compositeReportDefinitionDurableId,
      compositeReportDefinitions.length,
      isLoadingCrds,
      isLoadingCrdSectionInfo,
      crdDurableId,
    ],
  );

  const compositeSectionLoadWarning = useMemo(() => {
    if (!unresolvedTemplateSectionIds.length) {
      return null;
    }
    return formatMessage(messages.partialTemplateSectionsFailed, {
      ids: unresolvedTemplateSectionIds.join(', '),
    });
  }, [unresolvedTemplateSectionIds, formatMessage]);

  const selectedCrdHasNoTemplateSections =
    !isLoadingCrdSectionInfo &&
    !!crdDurableId &&
    hasTemplateSections(crdDurableId) === false;

  return {
    crdDurableId,
    setCrdDurableId,
    instructions,
    setInstructions,
    impressionType,
    setImpressionType,
    modelId,
    setModelId,
    showModelSelect,
    models: modelsQuery.data?.models ?? [],
    isLoadingCrds,
    compositeReportDefinitions,
    impressionTypes,
    isRegenerate,
    currentImpressionStatus,
    impressionWarning,
    generateDisabled: generateDisabled || awaitingCompositeCrdSelection,
    executiveSummary,
    isActionItemsRegenerate,
    patientId,
    onDismiss,
    isLoadingCrdSectionInfo,
    hasTemplateSections,
    selectedCrdHasNoTemplateSections,
    compositeSectionLoadWarning,
  };
}

export const GenerateMenuFormBody: React.FC<{
  form: GenerateMenuFormState;
}> = ({ form }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  return (
    <Box direction="column" gap={2} className={classes.modalBox}>
      {!form.isActionItemsRegenerate && !form.isLoadingCrds && (
        <>
          {form.isLoadingCrdSectionInfo ? (
            <Box direction="column" align="center">
              <DotLoader size={0} />
            </Box>
          ) : (
            <CRDSelect
              crdDurableId={form.crdDurableId}
              setCrdDurableId={form.setCrdDurableId}
              compositeReportDefinitions={form.compositeReportDefinitions}
              hasTemplateSections={form.hasTemplateSections}
            />
          )}
        </>
      )}
      {!form.isActionItemsRegenerate &&
        form.isRegenerate &&
        (form.impressionTypes.state === 'loaded' ? (
          <ImpressionTypeSelect
            availableImpressionTypes={form.impressionTypes.data}
            impressionType={form.impressionType}
            setImpressionType={form.setImpressionType}
          />
        ) : (
          <Box direction="column" align="center">
            <DotLoader size={0} />
          </Box>
        ))}
      <Divider />
      <Instructions
        instructions={form.instructions}
        setInstructions={form.setInstructions}
        isRequired={form.currentImpressionStatus === 'human-edited'}
      />
      {form.showModelSelect && (
        <ModelSelect
          modelId={form.modelId}
          setModelId={form.setModelId}
          models={form.models}
        />
      )}
      {form.selectedCrdHasNoTemplateSections && (
        <Alert statusType="warning">
          <AlertIcon icon={AlertCircle} />
          <AlertBody>
            <Text size="caption" weight="bold">
              {formatMessage(messages.crdNoTemplateSectionsWarning)}
            </Text>
          </AlertBody>
        </Alert>
      )}
      {form.impressionWarning && (
        <Alert statusType="warning">
          <AlertIcon icon={AlertCircle} />
          <AlertBody>
            <Text size="caption" weight="bold">
              {form.impressionWarning}
            </Text>
          </AlertBody>
        </Alert>
      )}
      {form.compositeSectionLoadWarning && (
        <Alert statusType="warning">
          <AlertIcon icon={AlertCircle} />
          <AlertBody>
            <Text size="caption" weight="bold">
              {form.compositeSectionLoadWarning}
            </Text>
          </AlertBody>
        </Alert>
      )}
    </Box>
  );
};

export const GenerateMenuAction: React.FC<{
  form: GenerateMenuFormState & GenerateMenuActionContext;
}> = ({ form }) => {
  if (form.isActionItemsRegenerate) {
    return (
      <RegenerateActionItems
        patientId={form.patientId}
        crdDurableId={form.crdDurableId}
        onGenerate={form.onDismiss}
      />
    );
  }
  if (form.impressionTypes.state === 'loaded') {
    return (
      <GenerateExecutiveSummary
        patientId={form.patientId}
        isRegenerate={form.isRegenerate}
        crdDurableId={form.crdDurableId}
        instructions={form.instructions}
        impressionTypeToGenerate={form.impressionType}
        availableImpressionTypes={form.impressionTypes.data.filter(
          (i): i is KnownImpressionTypes => i !== 'all',
        )}
        modelId={form.modelId}
        onGenerate={form.onDismiss}
        disabled={form.generateDisabled}
      />
    );
  }
  return <GenerateExecutiveSummary.Disabled isRegenerate={form.isRegenerate} />;
};

function GenerateMenuModal({
  isOpen,
  onDismiss,
  patientId,
  executiveSummary,
  isActionItemsRegenerate = false,
}: GenerateMenuModalProps) {
  const { formatMessage } = useIntl();

  const form = useGenerateMenuForm({
    onDismiss,
    patientId,
    executiveSummary,
    isActionItemsRegenerate,
  });

  return (
    <Modal
      title={formatMessage(messages.generate)}
      isOpen={isOpen}
      onDismiss={onDismiss}
      size={1}
      actions={
        <>
          <Button
            onClick={onDismiss}
            variant="outlined"
            color="negative"
            fullWidth
          >
            {executiveSummary
              ? formatMessage(messages.cancel)
              : formatMessage(messages.exit)}
          </Button>
          <GenerateMenuAction form={form} />
        </>
      }
    >
      <GenerateMenuFormBody form={form} />
    </Modal>
  );
}

type UseImpressionWarningArgs = {
  currentImpressionStatus: 'ai-generated' | 'human-edited' | 'missing';
  isRegenerate: boolean;
  executiveSummary: ExecutiveSummaryData | undefined;
  isActionItemsRegenerate: boolean;
  selectedImpressionType: ImpressionType;
  hasInstructions: boolean;
};

type UseImpressionWarningResult = {
  warning: string | null;
  generateDisabled: boolean;
};

function useImpressionWarning({
  currentImpressionStatus,
  isRegenerate,
  executiveSummary,
  isActionItemsRegenerate,
  selectedImpressionType,
  hasInstructions,
}: UseImpressionWarningArgs): UseImpressionWarningResult {
  const { formatMessage } = useIntl();

  // prevent generating a human-edited impression if the impression is not all
  if (
    selectedImpressionType !== 'all' &&
    currentImpressionStatus === 'human-edited' &&
    !hasInstructions
  ) {
    return {
      warning: formatMessage(messages.impressionV2Error),
      generateDisabled: true,
    };
  }

  // allow generating a new summary if the executive summary is not active
  if (!isRegenerate && !isActionItemsRegenerate && executiveSummary) {
    return {
      warning: formatMessage(messages.generateNewAlert, {
        status: executiveSummary.status,
      }),
      generateDisabled: false,
    };
  }

  // warn user that edited sections will not be regenerated if no instructions are provided
  if (isRegenerate && !hasInstructions && selectedImpressionType === 'all') {
    return {
      warning: formatMessage(messages.regenerateAlert),
      generateDisabled: false,
    };
  }

  // warn user that edited sections will be regenerated using provided instructions
  if (isRegenerate && hasInstructions) {
    return {
      warning: formatMessage(messages.regenerateAllV2WithInstructions),
      generateDisabled: false,
    };
  }

  return {
    warning: null,
    generateDisabled: false,
  };
}

export { GenerateMenuModal };
